import firebase from "firebase/app";
import "firebase/auth"

const baseUrl = "https://rest.publisher.zone/";

// The backend deliberately avoids meaningful error codes, so every failure is
// mapped to one of these kinds here and nowhere else. If the API ever gets
// proper codes, only classify() needs to change.
export const ApiErrorKind = Object.freeze({
    AUTH: "auth",             // could not obtain a firebase token
    NETWORK: "network",       // request never got a response (offline, CORS, DNS, ...)
    HTTP: "http",             // non-2xx response
    PARSE: "parse",           // 2xx response but body was not valid JSON
    NO_ACCOUNT: "noAccount",  // backend says the user has no linked account
})

// Messages the backend uses instead of status codes
const NO_ACCOUNT_MESSAGES = ["Bad Request/No Data found"]

function classify(status, body) {
    if (body && NO_ACCOUNT_MESSAGES.includes(body.message)) return ApiErrorKind.NO_ACCOUNT
    if (status < 200 || status >= 300) return ApiErrorKind.HTTP
    return null
}

/**
 * Never throws. Always resolves to
 * { ok, status, body, kind, error }
 * where `kind` is null on success, otherwise one of ApiErrorKind.
 */
export async function apiRequest(method, apifunction, params, authenticated, blob) {
    const headers = {
        "Content-type": "application/json"
    }

    if (authenticated && firebase.auth().currentUser) {
        try {
            headers["token"] = await firebase.auth().currentUser.getIdToken()
        } catch (error) {
            return {ok: false, status: 0, body: null, kind: ApiErrorKind.AUTH, error}
        }
    }

    const init = {
        method: method,
        headers: headers
    }

    if (params) {
        init.body = JSON.stringify(params)
    }

    let response
    try {
        response = await fetch(baseUrl + apifunction, init)
    } catch (error) {
        return {ok: false, status: 0, body: null, kind: ApiErrorKind.NETWORK, error}
    }

    if (blob && response.ok) {
        try {
            return {ok: true, status: response.status, body: await response.blob(), kind: null, error: null}
        } catch (error) {
            return {ok: false, status: response.status, body: null, kind: ApiErrorKind.NETWORK, error}
        }
    }

    let body = null
    let parseError = null
    try {
        body = await response.json()
    } catch (error) {
        parseError = error
    }

    const kind = classify(response.status, body) || (parseError ? ApiErrorKind.PARSE : null)
    return {ok: kind === null, status: response.status, body, kind, error: parseError}
}

/**
 * Legacy contract, kept for existing callers: resolves to the parsed body
 * (or blob) on 2xx, null on non-2xx, and rejects on network/token/parse errors.
 */
export async function RequestAPI(method, apifunction, params, authenticated, blob) {
    const result = await apiRequest(method, apifunction, params, authenticated, blob)

    if (result.kind === ApiErrorKind.AUTH || result.kind === ApiErrorKind.NETWORK || result.kind === ApiErrorKind.PARSE) {
        throw result.error
    }
    if (result.status < 200 || result.status >= 300) {
        return null
    }
    return result.body
}

export default RequestAPI
